import React, { useEffect, useState, useMemo } from "react";
import {
  adminListAgencies,
  adminCreateAgency,
  adminDeleteAgency,
  adminUpdateAgency,
  adminResetAgencyPassword,
  adminSetAgencyActive,
} from "../lib/api";
import {
  Building2, Trash2, Plus, Network, Pencil, KeyRound, Power, PowerOff,
  Phone, MapPin, User as UserIcon, FileText, Save, X, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { EMERGENCY_FR } from "../i18n.jsx";
import { useT } from "../lib/lang";

// All emergency categories that can be assigned to an Agence. We expose
// the full set rather than the original 4-way Medical/Fire/Police/FAMA
// pick so the Phase-1 multi-category model can carry every emergency
// type that exists in the citizen flow.
const ALL_CATEGORIES = Object.keys(EMERGENCY_FR);

const emptyForm = () => ({
  name: "",
  email: "",
  password: "",
  emergency_type: "Medical",
  phone: "",
  contact_name: "",
  categories: ["Medical"],
  region: "",
  active: true,
  notes: "",
});

// Shared subset of the create form used in BOTH the create + edit forms
// to keep the layout consistent.
const FormFields = ({ form, setForm, includePrimary }) => {
  // Multi-category chips — clicking toggles the category in/out. The
  // primary `emergency_type` chip is always pinned in the list so the
  // single-type legacy field stays in sync.
  const toggleCat = (c) => setForm((f) => {
    const has = f.categories.includes(c);
    return { ...f, categories: has ? f.categories.filter((x) => x !== c) : [...f.categories, c] };
  });

  return (
    <div className="space-y-2">
      <input
        data-testid="admin-agency-name-input"
        value={form.name}
        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        placeholder="Nom de l'Agence — ex. Agence Sécurité"
        className="w-full border border-zinc-700 bg-black/40 text-zinc-100 placeholder-zinc-600 focus:border-emerald-500 focus:outline-none px-2 py-2 text-sm"
      />
      <input
        data-testid="admin-agency-email-input"
        type="email"
        value={form.email}
        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
        placeholder="E-mail de connexion (ex. dispatch@samu.ml)"
        className="w-full border border-zinc-700 bg-black/40 text-zinc-100 placeholder-zinc-600 focus:border-emerald-500 focus:outline-none px-2 py-2 text-sm font-mono"
      />
      <div className="grid grid-cols-2 gap-2">
        <div className="relative">
          <Phone className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <input
            data-testid="admin-agency-phone-input"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            placeholder="Téléphone (+223…)"
            className="w-full border border-zinc-700 bg-black/40 text-zinc-100 placeholder-zinc-600 focus:border-emerald-500 focus:outline-none pl-7 pr-2 py-2 text-sm font-mono"
          />
        </div>
        <div className="relative">
          <UserIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <input
            data-testid="admin-agency-contact-input"
            value={form.contact_name}
            onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
            placeholder="Responsable"
            className="w-full border border-zinc-700 bg-black/40 text-zinc-100 placeholder-zinc-600 focus:border-emerald-500 focus:outline-none pl-7 pr-2 py-2 text-sm"
          />
        </div>
      </div>
      <div className="relative">
        <MapPin className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
        <input
          data-testid="admin-agency-region-input"
          value={form.region}
          onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
          placeholder="Région / commune d'intervention"
          className="w-full border border-zinc-700 bg-black/40 text-zinc-100 placeholder-zinc-600 focus:border-emerald-500 focus:outline-none pl-7 pr-2 py-2 text-sm"
        />
      </div>
      {includePrimary && (
        <div>
          <label className="text-[10px] font-bold tracking-[0.25em] uppercase text-zinc-500">
            Catégorie principale
          </label>
          <div className="grid grid-cols-4 gap-1 mt-1">
            {["Medical", "Fire", "Police", "FAMA"].map((tp) => (
              <button
                key={tp}
                type="button"
                data-testid={`admin-agency-type-${tp}`}
                onClick={() => setForm((f) => ({
                  ...f,
                  emergency_type: tp,
                  // Auto-include the primary in the multi-cat list so
                  // they never go out of sync.
                  categories: f.categories.includes(tp) ? f.categories : [...f.categories, tp],
                }))}
                className={`py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] border transition-colors ${
                  form.emergency_type === tp
                    ? "bg-emerald-500 text-black border-emerald-500"
                    : "border-zinc-700 text-zinc-400 hover:border-emerald-500 hover:text-emerald-300"
                }`}
              >
                {EMERGENCY_FR[tp]?.label || tp}
              </button>
            ))}
          </div>
        </div>
      )}
      <div>
        <label className="text-[10px] font-bold tracking-[0.25em] uppercase text-zinc-500">
          Catégories assignées ({form.categories.length})
        </label>
        <div className="flex flex-wrap gap-1 mt-1" data-testid="admin-agency-categories">
          {ALL_CATEGORIES.map((c) => {
            const active = form.categories.includes(c);
            return (
              <button
                key={c}
                type="button"
                data-testid={`admin-agency-cat-${c}`}
                onClick={() => toggleCat(c)}
                className={`px-2 py-1 text-[10px] font-bold uppercase tracking-[0.15em] border transition-colors ${
                  active
                    ? "bg-emerald-500 text-black border-emerald-500"
                    : "border-zinc-700 text-zinc-500 hover:border-emerald-500 hover:text-emerald-300"
                }`}
              >
                {EMERGENCY_FR[c]?.label || c}
              </button>
            );
          })}
        </div>
      </div>
      <div className="relative">
        <FileText className="absolute left-2 top-2 w-3.5 h-3.5 text-zinc-500" />
        <textarea
          data-testid="admin-agency-notes-input"
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          placeholder="Notes / description interne"
          rows={2}
          className="w-full border border-zinc-700 bg-black/40 text-zinc-100 placeholder-zinc-600 focus:border-emerald-500 focus:outline-none pl-7 pr-2 py-2 text-sm resize-none"
        />
      </div>
    </div>
  );
};

// -----------------------------------------------------------
// Inline edit drawer — opens above the card it's editing.
// Password is purposely absent. Use the "Réinitialiser MDP" button.
// -----------------------------------------------------------
const EditForm = ({ agency, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: agency.name || "",
    email: agency.email || "",
    password: "",  // unused — placeholder to satisfy FormFields shape
    emergency_type: agency.emergency_type || "Medical",
    phone: agency.phone || "",
    contact_name: agency.contact_name || "",
    categories: agency.categories?.length ? [...agency.categories] : [agency.emergency_type || "Medical"],
    region: agency.region || "",
    active: agency.active !== false,
    notes: agency.notes || "",
  });
  const [submitting, setSubmitting] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Nom et e-mail sont obligatoires");
      return;
    }
    setSubmitting(true);
    try {
      const patch = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        contact_name: form.contact_name.trim(),
        emergency_type: form.emergency_type,
        categories: form.categories,
        region: form.region.trim(),
        notes: form.notes.trim(),
      };
      const updated = await adminUpdateAgency(agency.id, patch);
      toast.success("Agence mise à jour");
      onSaved(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mise à jour impossible");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={save}
      className="border border-emerald-500/40 bg-[#0b0f14] p-3 space-y-2"
      data-testid={`admin-agency-edit-${agency.id}`}
    >
      <div className="flex items-center justify-between border-b border-emerald-500/20 pb-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-emerald-300 flex items-center gap-1.5">
          <Pencil className="w-3.5 h-3.5" /> Modifier — {agency.name}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="text-zinc-500 hover:text-zinc-200"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <FormFields form={form} setForm={setForm} includePrimary={true} />
      <div className="grid grid-cols-2 gap-1.5 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="border border-zinc-700 text-zinc-400 py-2 text-[10px] font-bold uppercase tracking-[0.15em] hover:border-zinc-500 hover:text-zinc-200 transition-colors"
        >
          Annuler
        </button>
        <button
          type="submit"
          data-testid={`admin-agency-save-${agency.id}`}
          disabled={submitting}
          className="bg-emerald-500 text-black py-2 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-emerald-400 transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
        >
          <Save className="w-3 h-3" />
          {submitting ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>
    </form>
  );
};

// -----------------------------------------------------------
// Password reset modal — single-purpose, no password disclosure.
// -----------------------------------------------------------
const ResetPasswordModal = ({ agency, onClose, onDone }) => {
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) { toast.error("Mot de passe trop court (min. 6 caractères)"); return; }
    if (pwd !== confirm) { toast.error("Les deux mots de passe ne correspondent pas"); return; }
    setSubmitting(true);
    try {
      await adminResetAgencyPassword(agency.id, pwd);
      toast.success(`Mot de passe réinitialisé pour ${agency.name}. Sessions actives révoquées.`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Réinitialisation impossible");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid={`admin-agency-reset-modal-${agency.id}`}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-sm border border-emerald-500/40 bg-[#0b0f14] p-4 space-y-3"
      >
        <div className="flex items-center gap-2 border-b border-emerald-500/20 pb-2">
          <KeyRound className="w-4 h-4 text-emerald-400" />
          <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-emerald-300">
            Réinitialiser le mot de passe
          </span>
        </div>
        <div className="text-xs text-zinc-400">
          Agence : <span className="text-zinc-100 font-bold">{agency.name}</span>
          <div className="font-mono text-[11px] text-zinc-500">{agency.email}</div>
        </div>
        <p className="text-[11px] text-amber-300/80 border-l-2 border-amber-400 pl-2">
          ⚠️ Le mot de passe actuel ne peut pas être affiché.
          Définissez ici un <strong>nouveau</strong> mot de passe.
          Toutes les sessions en cours seront révoquées.
        </p>
        <input
          data-testid="admin-agency-reset-new-pwd"
          type="text"
          autoComplete="new-password"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          placeholder="Nouveau mot de passe (≥ 6 caractères)"
          className="w-full border border-zinc-700 bg-black/40 text-zinc-100 placeholder-zinc-600 focus:border-emerald-500 focus:outline-none px-2 py-2 text-sm font-mono"
        />
        <input
          data-testid="admin-agency-reset-confirm-pwd"
          type="text"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Confirmer le nouveau mot de passe"
          className="w-full border border-zinc-700 bg-black/40 text-zinc-100 placeholder-zinc-600 focus:border-emerald-500 focus:outline-none px-2 py-2 text-sm font-mono"
        />
        <div className="grid grid-cols-2 gap-1.5 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="border border-zinc-700 text-zinc-400 py-2 text-[10px] font-bold uppercase tracking-[0.15em] hover:border-zinc-500 hover:text-zinc-200 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            data-testid={`admin-agency-reset-submit-${agency.id}`}
            disabled={submitting}
            className="bg-emerald-500 text-black py-2 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-emerald-400 transition-colors disabled:opacity-60"
          >
            {submitting ? "Réinitialisation…" : "Réinitialiser"}
          </button>
        </div>
      </form>
    </div>
  );
};

// -----------------------------------------------------------
// Main tab
// -----------------------------------------------------------
export const AgenciesTab = () => {
  const [t] = useT();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);  // id of the agency whose edit drawer is open
  const [resetForId, setResetForId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try { setList(await adminListAgencies()); }
    catch (_e) { setList([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { Promise.resolve().then(refresh); }, []);

  const activeCount = useMemo(() => list.filter((a) => a.active !== false).length, [list]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      toast.error("Nom, e-mail et mot de passe sont obligatoires");
      return;
    }
    setSubmitting(true);
    try {
      await adminCreateAgency({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        emergency_type: form.emergency_type,
        phone: form.phone.trim() || undefined,
        contact_name: form.contact_name.trim() || undefined,
        categories: form.categories,
        region: form.region.trim() || undefined,
        notes: form.notes.trim() || undefined,
      });
      toast.success(t.adminAgencyCreated || "Agence créée");
      setForm(emptyForm());
      setShowForm(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Création impossible");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Supprimer définitivement « ${a.name} » ? Cette action est irréversible.`)) return;
    try {
      await adminDeleteAgency(a.id);
      toast.success("Agence supprimée");
      setList((prev) => prev.filter((x) => x.id !== a.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Suppression impossible");
    }
  };

  const toggleActive = async (a) => {
    const next = !(a.active !== false);
    try {
      const updated = await adminSetAgencyActive(a.id, next);
      toast.success(next ? `${a.name} réactivée` : `${a.name} désactivée — sessions révoquées`);
      setList((prev) => prev.map((x) => (x.id === a.id ? updated : x)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action impossible");
    }
  };

  return (
    <div className="px-3 sm:px-5 py-4 pb-8 space-y-3" data-testid="admin-agencies-tab">
      <div className="flex items-center gap-2 border-b border-zinc-800 pb-2">
        <Network className="w-4 h-4 text-emerald-400" strokeWidth={2.4} />
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-300">
          Réseau de dispatch
        </span>
        <span className="ml-auto font-mono text-[10px] text-zinc-500 tabular-nums">
          <span className="text-emerald-300 font-bold">{activeCount}</span>
          <span className="text-zinc-600"> / </span>
          <span className="text-zinc-400">{list.length}</span> AGENCE{list.length === 1 ? "" : "S"}
        </span>
      </div>

      <p className="text-[11px] text-zinc-500">
        Gérez les Agences internes (Sécurité, Médicale, Incendie, etc.) — création, modification, réinitialisation du mot de passe, activation / désactivation.
      </p>

      {!showForm && (
        <button
          type="button"
          data-testid="admin-add-agency-btn"
          onClick={() => setShowForm(true)}
          className="w-full border border-emerald-500/50 text-emerald-300 py-2.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-emerald-500 hover:text-black transition-colors flex items-center justify-center gap-1.5"
        >
          <Plus className="w-3.5 h-3.5" /> Nouvelle Agence
        </button>
      )}

      {showForm && (
        <form
          onSubmit={create}
          className="border border-emerald-500/40 bg-[#0f1318] p-3 space-y-2"
          data-testid="admin-agency-form"
        >
          <div className="flex items-center justify-between border-b border-emerald-500/20 pb-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-emerald-300 flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Nouvelle Agence
            </span>
            <button
              type="button"
              onClick={() => { setShowForm(false); setForm(emptyForm()); }}
              aria-label="Fermer"
              className="text-zinc-500 hover:text-zinc-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <FormFields form={form} setForm={setForm} includePrimary={true} />
          <input
            data-testid="admin-agency-password-input"
            type="text"
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            placeholder="Mot de passe (≥ 4 caractères, défini par l'administrateur)"
            className="w-full border border-amber-500/40 bg-black/40 text-zinc-100 placeholder-zinc-600 focus:border-amber-400 focus:outline-none px-2 py-2 text-sm font-mono"
          />
          <div className="grid grid-cols-2 gap-1.5 pt-1">
            <button
              type="button"
              onClick={() => { setShowForm(false); setForm(emptyForm()); }}
              className="border border-zinc-700 text-zinc-400 py-2 text-[10px] font-bold uppercase tracking-[0.15em] hover:border-zinc-500 hover:text-zinc-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              data-testid="admin-agency-submit"
              disabled={submitting}
              className="bg-emerald-500 text-black py-2 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-emerald-400 transition-colors disabled:opacity-60"
            >
              {submitting ? "Création…" : "Créer l'Agence"}
            </button>
          </div>
        </form>
      )}

      {loading && <div className="text-xs text-zinc-500">Chargement…</div>}
      {!loading && list.length === 0 && (
        <div className="text-xs text-zinc-500 py-4 text-center">Aucune Agence pour l'instant.</div>
      )}

      <div className="space-y-2">
        {list.map((a) => {
          const isActive = a.active !== false;
          const isEditing = editingId === a.id;
          const cats = (a.categories && a.categories.length) ? a.categories : [a.emergency_type];
          return (
            <div key={a.id} className="space-y-2">
              <div
                className={`border bg-[#0f1318] transition-colors p-3 ${
                  isActive ? "border-zinc-800 hover:border-emerald-500/40" : "border-zinc-800/60 opacity-60 hover:opacity-80"
                }`}
                data-testid={`admin-agency-${a.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <Building2 className={`w-3.5 h-3.5 ${isActive ? "text-emerald-400" : "text-zinc-600"}`} />
                      <div className="font-display text-base font-black tracking-tight truncate text-zinc-100">{a.name}</div>
                      <span
                        data-testid={`admin-agency-status-${a.id}`}
                        className={`text-[9px] font-bold uppercase tracking-[0.2em] px-1.5 py-0.5 border ${
                          isActive
                            ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                            : "border-rose-500/40 text-rose-300 bg-rose-500/10"
                        }`}
                      >
                        {isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div className="font-mono text-[11px] text-zinc-500 mt-0.5 truncate">{a.email}</div>
                    {a.contact_name && (
                      <div className="text-[11px] text-zinc-400 mt-0.5 flex items-center gap-1">
                        <UserIcon className="w-3 h-3 text-zinc-600" /> {a.contact_name}
                        {a.phone && <span className="text-zinc-600 ml-1 font-mono">· {a.phone}</span>}
                      </div>
                    )}
                    {!a.contact_name && a.phone && (
                      <div className="font-mono text-[11px] text-zinc-400 mt-0.5 flex items-center gap-1">
                        <Phone className="w-3 h-3 text-zinc-600" /> {a.phone}
                      </div>
                    )}
                    {a.region && (
                      <div className="text-[11px] text-zinc-400 mt-0.5 flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-zinc-600" /> {a.region}
                      </div>
                    )}
                    {a.notes && (
                      <div className="text-[11px] text-zinc-500 mt-1 italic line-clamp-2">{a.notes}</div>
                    )}
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {cats.map((c) => (
                        <span
                          key={c}
                          className="bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em]"
                        >
                          {EMERGENCY_FR[c]?.label || c}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => setEditingId(isEditing ? null : a.id)}
                      data-testid={`admin-agency-edit-btn-${a.id}`}
                      className="border border-emerald-500/40 text-emerald-300 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-emerald-500 hover:text-black transition-colors flex items-center gap-1"
                    >
                      <Pencil className="w-3 h-3" /> Modifier
                    </button>
                    <button
                      type="button"
                      onClick={() => setResetForId(a.id)}
                      data-testid={`admin-agency-reset-btn-${a.id}`}
                      className="border border-amber-500/40 text-amber-300 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-amber-500 hover:text-black transition-colors flex items-center gap-1"
                    >
                      <KeyRound className="w-3 h-3" /> MDP
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleActive(a)}
                      data-testid={`admin-agency-toggle-${a.id}`}
                      className={`border px-2 py-1 text-[10px] font-bold uppercase tracking-[0.15em] transition-colors flex items-center gap-1 ${
                        isActive
                          ? "border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                          : "border-emerald-500/50 text-emerald-300 hover:bg-emerald-500 hover:text-black"
                      }`}
                    >
                      {isActive ? <><PowerOff className="w-3 h-3" /> Désactiver</> : <><Power className="w-3 h-3" /> Activer</>}
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(a)}
                      data-testid={`admin-agency-delete-${a.id}`}
                      className="border border-rose-500/50 text-rose-300 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-rose-500 hover:text-white transition-colors flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" /> Supprimer
                    </button>
                  </div>
                </div>
                {isActive && (
                  <div className="mt-2 pt-2 border-t border-zinc-800 text-[10px] text-zinc-500 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3 text-emerald-500" />
                    Compte actif — connexion possible
                  </div>
                )}
              </div>
              {isEditing && (
                <EditForm
                  agency={a}
                  onClose={() => setEditingId(null)}
                  onSaved={(updated) => {
                    setList((prev) => prev.map((x) => (x.id === a.id ? updated : x)));
                    setEditingId(null);
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {resetForId && (() => {
        const target = list.find((x) => x.id === resetForId);
        if (!target) return null;
        return (
          <ResetPasswordModal
            agency={target}
            onClose={() => setResetForId(null)}
            onDone={() => setResetForId(null)}
          />
        );
      })()}
    </div>
  );
};
